import React, { useState, useEffect, useCallback } from 'react';
import { invWastageService, invItemService, invUnitService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtQty, fmtCost, fmtDate, friendlyErr } from './inventoryStyles';

const REASONS = ['spoilage','expired','kitchen_waste','damaged','spilled','theft_loss','internal_usage','other'];
const REASON_LABELS = { spoilage: 'Spoilage', expired: 'Expired', kitchen_waste: 'Kitchen Waste', damaged: 'Damaged', spilled: 'Spilled', theft_loss: 'Theft / Loss', internal_usage: 'Internal Usage', other: 'Other' };

const Wastage = ({ restaurant }) => {
  const [entries, setEntries]       = useState([]);
  const [items, setItems]           = useState([]);
  const [units, setUnits]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [form, setForm] = useState({ inventory_item_id: '', quantity: '', unit_id: '', reason: 'kitchen_waste', notes: '', wastage_date: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [eRes, iRes, uRes] = await Promise.all([
      invWastageService.getAll(restaurantId),
      invItemService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
    ]);
    setEntries(eRes.data || []);
    setItems((iRes.data || []).filter(i => i.is_active));
    setUnits(uRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setForm({ inventory_item_id: '', quantity: '', unit_id: '', reason: 'kitchen_waste', notes: '', wastage_date: new Date().toISOString().slice(0, 10) });
    setError(''); setShowForm(true);
  };

  const save = async () => {
    if (!form.inventory_item_id) { setError('Select an item.'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { setError('Quantity must be > 0.'); return; }
    setSaving(true); setError('');
    const { error: e } = await invWastageService.create(restaurantId, form);
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false); load();
  };

  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  };

  const selectedItem = items.find(i => i.id === form.inventory_item_id);
  const wastageValue = selectedItem && form.quantity
    ? parseFloat(form.quantity) * (selectedItem.avg_unit_cost || 0)
    : 0;

  // Summary: total wastage value this week
  const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - 7);
  const weeklyWaste = entries
    .filter(e => new Date(e.created_at) > weekStart)
    .reduce((s, e) => s + (e.quantity || 0) * (e.inventory_item?.avg_unit_cost || 0), 0);

  // Group entries by wastage_date, sorted newest first
  const byDate = entries.reduce((acc, e) => {
    const d = e.wastage_date || e.created_at?.slice(0, 10) || 'Unknown';
    if (!acc[d]) acc[d] = [];
    acc[d].push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Wastage</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Track and analyse stock wastage · Last 7 days: <strong style={{ color: '#dc2626' }}>{fmtCost(weeklyWaste)}</strong></p>
          </div>
          <button style={btn('primary')} onClick={openForm}>+ Record Wastage</button>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : sortedDates.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No wastage recorded</div>
            </div>
          ) : sortedDates.map(date => {
            const dayEntries = byDate[date];
            const dayTotal   = dayEntries.reduce((s, e) => s + (e.quantity || 0) * (e.inventory_item?.avg_unit_cost || 0), 0);
            const isOpen     = expandedDates.has(date);

            return (
              <div key={date} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {/* Date header row — clickable */}
                <div
                  onClick={() => toggleDate(date)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px', cursor: 'pointer', background: isOpen ? '#fef9f9' : 'white',
                    userSelect: 'none',
                  }}
                  onMouseOver={e => { if (!isOpen) e.currentTarget.style.background = '#fafafa'; }}
                  onMouseOut={e => { if (!isOpen) e.currentTarget.style.background = 'white'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '1.1rem', color: isOpen ? '#dc2626' : '#555', fontWeight: '400' }}>
                      {isOpen ? '▾' : '▸'}
                    </span>
                    <span style={{ fontWeight: '700', color: '#222', fontSize: '0.95rem' }}>{fmtDate(date)}</span>
                    <span style={{
                      background: '#f3f4f6', color: '#555', borderRadius: '20px',
                      padding: '2px 10px', fontSize: '0.78rem', fontWeight: '600',
                    }}>
                      {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                    </span>
                  </div>
                  <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '0.95rem' }}>
                    {fmtCost(dayTotal)}
                  </span>
                </div>

                {/* Expanded detail rows */}
                {isOpen && (
                  <div style={{ background: '#fef9f9', borderTop: '1px solid #fee2e2' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                      <thead>
                        <tr style={{ background: '#fff1f1' }}>
                          {['Item', 'Qty', 'Reason', 'Wastage Value', 'Notes'].map(h => (
                            <th key={h} style={{ padding: '10px 18px', textAlign: 'left', fontWeight: '700', color: '#991b1b', fontSize: '0.78rem' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dayEntries.map((e, idx) => (
                          <tr key={e.id} style={{ borderTop: idx > 0 ? '1px solid #fee2e2' : 'none' }}>
                            <td style={{ padding: '11px 18px', fontWeight: '600', color: '#333' }}>{e.inventory_item?.name || '—'}</td>
                            <td style={{ padding: '11px 18px', color: '#dc2626', fontWeight: '700' }}>
                              {fmtQty(e.quantity)} {e.unit?.short_code || ''}
                            </td>
                            <td style={{ padding: '11px 18px', color: '#555' }}>{REASON_LABELS[e.reason] || e.reason}</td>
                            <td style={{ padding: '11px 18px', color: '#dc2626', fontWeight: '600' }}>
                              {fmtCost((e.quantity || 0) * (e.inventory_item?.avg_unit_cost || 0))}
                            </td>
                            <td style={{ padding: '11px 18px', color: '#888' }}>{e.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '440px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>Record Wastage</h3>
            {error && <div style={errorBox}>{error}</div>}

            <div style={fieldGroup}>
              <label style={label}>Item *</label>
              <select style={selectStyle(!form.inventory_item_id)} value={form.inventory_item_id} onChange={e => setForm(f => ({ ...f, inventory_item_id: e.target.value }))}>
                <option value="">Select item…</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name} (stock: {fmtQty(i.current_stock)})</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={fieldGroup}>
                <label style={label}>Quantity *</label>
                <input style={inputStyle(!form.quantity)} type="number" min="0.001" step="0.001" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0.000" />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Unit</label>
                <select style={selectStyle()} value={form.unit_id} onChange={e => setForm(f => ({ ...f, unit_id: e.target.value }))}>
                  <option value="">Base unit</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.short_code}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Reason *</label>
                <select style={selectStyle()} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                  {REASONS.map(r => <option key={r} value={r}>{REASON_LABELS[r]}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Date</label>
                <input style={inputStyle()} type="date" value={form.wastage_date} onChange={e => setForm(f => ({ ...f, wastage_date: e.target.value }))} />
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Notes</label>
              <input style={inputStyle()} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional description…" />
            </div>

            {wastageValue > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '0.88rem' }}>
                Estimated wastage value: <strong style={{ color: '#dc2626' }}>{fmtCost(wastageValue)}</strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Record Wastage'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wastage;
